import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Col, Descriptions, Form, InputNumber, Modal, Row, Select, Space, message } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

const money = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);

export default function TransferTenantModal({ application, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const propertySequence = useRef(0);
  const unitSequence = useRef(0);
  const quoteSequence = useRef(0);
  const propId = Form.useWatch("propId", form);
  const unitId = Form.useWatch("unitId", form);

  const searchProperties = async (content = "") => {
    const sequence = ++propertySequence.current;
    setPropertyLoading(true);
    setProperties([]);
    try {
      const result = await http.get(api.getPropSearch, { params: { content } });
      if (sequence !== propertySequence.current) return;
      if (result.code !== "1") throw new Error();
      setProperties((result.data || []).map((item) => ({ value: item.propId, label: item.propName || item.fullDisplay || item.prop })));
    } catch (_error) {
      if (sequence === propertySequence.current) message.error("Could not load properties");
    } finally {
      if (sequence === propertySequence.current) setPropertyLoading(false);
    }
  };

  const searchUnits = async (propertyId, content = "") => {
    const sequence = ++unitSequence.current;
    setUnits([]);
    setUnitLoading(Boolean(propertyId));
    if (!propertyId) return;
    try {
      const result = await http.get(api.unitSearch, { params: { propId: propertyId, content } });
      if (sequence !== unitSequence.current) return;
      if (result.code !== "1") throw new Error();
      setUnits((result.data || []).map((item) => ({ value: item.unitId,
        label: item.unitName || item.unit, disabled: item.unitId === application.unitId })));
    } catch (_error) {
      if (sequence === unitSequence.current) message.error("Could not load units");
    } finally {
      if (sequence === unitSequence.current) setUnitLoading(false);
    }
  };

  const loadQuote = async (id) => {
    const sequence = ++quoteSequence.current;
    setQuote(null);
    setConfirmed(false);
    setQuoteLoading(Boolean(id));
    if (!id) return;
    try {
      const result = await http.get(api.creditCheckUnitQuote, { params: { unitId: id } });
      if (sequence !== quoteSequence.current) return;
      if (result.code !== "1") throw new Error();
      setQuote({ ...result.data, unitId: id });
    } catch (_error) {
      if (sequence === quoteSequence.current) message.error("Could not load unit amounts. Retry using Refresh amounts.");
    } finally {
      if (sequence === quoteSequence.current) setQuoteLoading(false);
    }
  };

  useEffect(() => {
    searchProperties();
    return () => {
      propertySequence.current += 1;
      unitSequence.current += 1;
      quoteSequence.current += 1;
    };
  }, []);

  const submit = async () => {
    if (inFlight.current || !confirmed || !quote || quote.unitId !== form.getFieldValue("unitId")) return;
    inFlight.current = true;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const result = await http.post(api.creditCheckTransferTenant, {
        applicationId: application.applicationId, sourceUnitId: application.unitId,
        tenantId: application.tenantId, unitId: values.unitId,
        rent: quote.rentRate, deposit: quote.deposit, additionalDeposit: values.additionalDeposit || 0,
      });
      if (result.code !== "1") {
        message.warning(result.msg || "Transfer could not be completed. Refresh the application and try again.");
        setConfirmed(false);
        return;
      }
      message.success("Tenant transferred");
      onSaved();
    } catch (error) {
      if (!error.errorFields) message.error("Could not complete transfer. Refresh application details before retrying.");
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return <Modal open centered width={800} title="Transfer tenant" className="cc-detail-modal cc-transfer-modal"
    maskClosable={false} closable={!saving} keyboard={!saving} onCancel={onClose}
    footer={<Space><Button disabled={saving} onClick={onClose}>Cancel</Button>
      <Button type="primary" loading={saving} disabled={!confirmed || !quote || quoteLoading}
        onClick={submit}>Transfer tenant</Button></Space>}>
    <div className="cc-transfer-source">
      <div><span>Current home · Application #{application.applicationId}</span>
        <strong>{application.prop} / {application.unit}</strong></div><ArrowRightOutlined />
      <div><span>Destination</span><strong>{quote ? `${quote.prop} / ${quote.unit}` : "Choose a new home"}</strong></div>
    </div>
    <Descriptions size="small" column={2}>
      <Descriptions.Item label="Current rent">{money(application.newRent)}</Descriptions.Item>
      <Descriptions.Item label="Current deposit">{money(Number(application.deposit || 0) + Number(application.additionalDeposit || 0))}</Descriptions.Item>
    </Descriptions>
    <Form form={form} layout="vertical" initialValues={{ additionalDeposit: application.additionalDeposit || 0 }}
      disabled={saving} onValuesChange={() => setConfirmed(false)}>
      <Row gutter={16}>
        <Col span={12}><Form.Item name="propId" label="Destination property" rules={[{ required: true }]}>
          <Select showSearch allowClear filterOption={false} options={properties} loading={propertyLoading}
            onSearch={searchProperties} onChange={(id) => {
              form.setFieldValue("unitId", undefined);
              loadQuote(null);
              searchUnits(id);
            }} placeholder="Choose property" />
        </Form.Item></Col>
        <Col span={12}><Form.Item name="unitId" label="Destination unit" rules={[{ required: true }]}>
          <Select showSearch filterOption={false} options={units} loading={unitLoading} disabled={!propId || saving}
            onSearch={(content) => searchUnits(propId, content)} onChange={loadQuote} placeholder="Choose unit" />
        </Form.Item></Col>
        <Col span={8}><Form.Item label="New base rent"><div className="cc-readonly-value">{quote ? money(quote.rentRate) : "—"}</div></Form.Item></Col>
        <Col span={8}><Form.Item label="Deposit"><div className="cc-readonly-value">{quote ? money(quote.deposit) : "—"}</div></Form.Item></Col>
        <Col span={8}><Form.Item name="additionalDeposit" label="Deposit add"><InputNumber prefix="$" min={0} max={65000} precision={2} style={{ width: "100%" }} /></Form.Item></Col>
      </Row>
      <Button type="link" disabled={!unitId || saving} loading={quoteLoading} onClick={() => loadQuote(unitId)}>Refresh amounts</Button>
    </Form>
    <Alert type="info" showIcon message="Existing billing and invoice amounts stay unchanged"
      description="The tenant moves to the selected unit and the previous unit becomes vacant. If payments exist, the destination must be in the same trust. Availability is checked again when you transfer." />
    <Checkbox className="cc-transfer-confirm" checked={confirmed} disabled={!quote || saving || quoteLoading}
      onChange={(event) => setConfirmed(event.target.checked)}>I have reviewed the destination and amounts.</Checkbox>
  </Modal>;
}
